import React, { useState } from 'react';

import { isDoublesDiscipline } from '../leagues/discipline';
import ScoringMatchCard from '../leagues/scoring-match-card';
import { filterMatches, matchesOfPlayer } from '../leagues/scoring-match-query';
import ScoringPlayerMatchesModal from '../leagues/scoring-player-matches-modal';
import { orderedRuns, runLabel, runsOfMatches } from '../leagues/scoring-runs';
import ScoringSearchBox from '../leagues/scoring-search-box';
import ScoringSectionLine, { MATCH_UNITS, PAIR_UNITS, PLAYER_UNITS, countLabel } from '../leagues/scoring-section-line';
import ScoringStandings from '../leagues/scoring-standings';
import { groupFormatLabel, ratingWeightLabel, usesPointSystem, type MatchItem } from '../leagues/scoring-types';
import TiebreakNote from '../leagues/tiebreak-note';
import { usePublicJson } from '../utils/use-public-json';
import { swissGroupOf, swissMatchesOf, type SwissView } from './swiss-view';

interface SwissGroupPanelProps {
  categoryId: string | number;
  heading: 'h1' | 'h2';
}

function roundsLabel(view: SwissView): string {
  if (view.tournament.roundCount === null) {
    return `${view.rounds.length} rund`;
  }

  return `runda ${view.rounds.length} z ${view.tournament.roundCount}`;
}

function matchCard(match: MatchItem, view: SwissView): JSX.Element {
  return <ScoringMatchCard key={match.id} match={match} canSubmit={false} scoring={view.scoring} showRating />;
}

const SwissGroupPanel: React.FC<SwissGroupPanelProps> = ({ categoryId, heading }) => {
  const { data: view, loading, error } = usePublicJson<SwissView>(`/api/public/categories/${categoryId}/swiss`);
  const [query, setQuery] = useState('');
  const [pickedPlayerId, setPickedPlayerId] = useState<number | null>(null);

  if (loading) {
    return <div className="participants-loading">Ładowanie...</div>;
  }

  if (error || !view) {
    return <div className="participants-error">Nie udało się załadować rund.</div>;
  }

  const doubles = isDoublesDiscipline(view.category.discipline);
  const weightLabel = ratingWeightLabel(view.ratingWeight);
  const group = swissGroupOf(view);
  const matches = swissMatchesOf(view);
  const visibleMatches = filterMatches(matches, query);
  const runs = orderedRuns(runsOfMatches(visibleMatches), 'runda') ?? [];
  const pickedPlayer = group.standings.find((row) => row.leaguePlayerId === pickedPlayerId) ?? null;
  const Heading = heading;

  return (
    <div className="swiss-group">
      <Heading className="swiss-group-name">{view.category.name}</Heading>
      <p className="swiss-page-meta">
        System szwajcarski · {countLabel(view.entrantCount, doubles ? PAIR_UNITS : PLAYER_UNITS)} · {roundsLabel(view)}
        {weightLabel && <span data-testid="swiss-rating-weight"> · ⚖️ Ranking {weightLabel}</span>}
      </p>

      <ScoringSectionLine title="Tabela" count={countLabel(group.standings.length, doubles ? PAIR_UNITS : PLAYER_UNITS)}>
        <TiebreakNote
          hint={group.tiebreak?.hint}
          steps={group.tiebreak?.steps}
          formatLabel={groupFormatLabel(group)}
          testId="swiss-standings-note"
        />
      </ScoringSectionLine>
      <ScoringStandings
        rows={group.standings}
        showPoints={usesPointSystem(group)}
        showPlacement={false}
        doubles={doubles}
        tiebreakHint={group.tiebreak?.hint}
        testId="swiss-standings"
        onPickPlayer={(row) => setPickedPlayerId(row.leaguePlayerId)}
      />
      {group.standings.length > 0 && (
        <p className="scoring-hint">Kliknij zawodnika w tabeli — zobaczysz jego ranking i mecze w turnieju.</p>
      )}

      <ScoringSectionLine title="Rundy" count={countLabel(matches.length, MATCH_UNITS)}>
        <ScoringSearchBox value={query} onChange={setQuery} />
      </ScoringSectionLine>

      {matches.length === 0 && <p>Pierwsza runda nie została jeszcze skojarzona.</p>}
      {visibleMatches.length === 0 && query.trim() !== '' && (
        <p className="scoring-no-matches" data-testid="scoring-search-empty">
          Brak meczów dla „{query}".
        </p>
      )}

      <ul className="scoring-matches" data-testid="swiss-matches">
        {runs.map((run) => (
          <React.Fragment key={run.number}>
            <li className="scoring-run-head" data-testid={`swiss-run-${run.number}`}>
              {runLabel(run, 'runda')}
            </li>
            {run.matches.map((match) => matchCard(match, view))}
          </React.Fragment>
        ))}
      </ul>

      {pickedPlayer !== null && (
        <ScoringPlayerMatchesModal
          player={pickedPlayer}
          matches={matchesOfPlayer(matches, pickedPlayer.leaguePlayerId)}
          showPoints={usesPointSystem(group)}
          showPlacement={false}
          ratingNote="ranking po rozegranych meczach (szacunkowo)"
          scoring={group.scoring}
          doubles={doubles}
          formatLabel={groupFormatLabel(group)}
          onClose={() => setPickedPlayerId(null)}
        />
      )}
    </div>
  );
};

export default SwissGroupPanel;
